import { useEffect, useState } from 'react'
import { Plus, Sparkles } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { SettingsCrumb } from '../admin/SettingsCrumb.js'
import { adminMutate, apiGet, usePermissions, useToast } from '../admin/primitives.js'
import { Field, NumberInput, SwitchRow } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { VenueWizard } from './VenueWizard.js'
import { VenueScanDrawer } from './VenueScanPanel.js'
import { InstagramProviderPanel } from './InstagramProviderPanel.js'
import { WebSearchPanel } from './WebSearchPanel.js'
import { RoomsDjsPanel } from './RoomsDjsPanel.js'
import { VenueDetailModal } from './VenueDetailModal.js'

/**
 * /settings/soirees — RÉGLAGES DU DOMAINE SOIRÉES (v4).
 *
 * Saisons, familles, salles, prévisions — et la VEILLE : établissements
 * surveillés avec leurs sources (santé affichée honnêtement), leurs
 * recherches AUTO (générées par Gestion) et CUSTOM, les mots-clés globaux,
 * et l'observabilité des scans. Les valeurs vivent dans instance_setting
 * et les tables de veille — jamais en dur.
 */

interface SettingsPayload {
  seasons: { min_gap_days: number; min_weekends: number; overrides: { name: string; start: string; end: string }[] }
  families: string[]
  mc_rooms: Record<string, string>
  market: { enabled: boolean; radius_km: number; center: string; keywords?: string[] }
  forecast: { enabled: boolean }
  /** Où la caisse de la nuit s'affiche dans les Soirées (15/10/2026). Absent d'un serveur d'avant : les défauts. */
  night_cash?: CaisseReglages
  detected_seasons: { name: string; start: string; end: string; boundary_source: string; events_count?: number }[]
}
/** Les interrupteurs de la Caisse de la nuit — miroir de `NightCashSettings` (core). */
interface CaisseReglages {
  soiree_page: boolean; week_cards: boolean; history: boolean; calendar: boolean; closed_revenue: boolean
}
/** Les défauts du code : tout allumé (décision de l'exploitant, 15/10/2026). */
const CAISSE_DEFAUT: CaisseReglages = { soiree_page: true, week_cards: true, history: true, calendar: true, closed_revenue: true }
const memeCaisse = (a: CaisseReglages, b: CaisseReglages): boolean =>
  (Object.keys(CAISSE_DEFAUT) as Array<keyof CaisseReglages>).every((k) => a[k] === b[k])

interface VenueRow {
  id: string; name: string; city: string | null; distance_km: string | null
  status: string; enabled: boolean; sources_count: number; queries_count: number
  category: string; priority_level: string; competition_class: string
  last_fetched_at: string | null; last_error: string | null
  scanning?: boolean
  last_scan?: {
    at: string; status: string; signals: number; new_signals: number
    events: number; errors: number; next_at: string | null
  } | null
}

/** « il y a 12 min » / « dans 1 h 23 » — la distance, pas l'horodatage brut. */
function distanceFr(iso: string | null, futur = false): string | null {
  if (!iso) return null
  const delta = futur ? new Date(iso).getTime() - Date.now() : Date.now() - new Date(iso).getTime()
  if (delta < 0) return futur ? 'imminent' : 'à l’instant'
  const min = Math.floor(delta / 60_000)
  if (min < 1) return futur ? 'dans moins d’une minute' : 'il y a quelques secondes'
  if (min < 60) return futur ? `dans ${min} min` : `il y a ${min} min`
  const h = Math.floor(min / 60)
  const reste = min % 60
  const txt = reste > 0 ? `${h} h ${String(reste).padStart(2, '0')}` : `${h} h`
  return futur ? `dans ${txt}` : `il y a ${txt}`
}
interface VenueDetail {
  venue: {
    id: string; name: string; city: string | null; website: string | null
    distance_km: number | null; status: string; enabled: boolean
    category: string; priority_level: string; competition_class: string; notes: string | null
    instagram_handle: string | null; custom_terms: string[]
  }
  sources: {
    id: string; type: string; label: string | null; url: string | null
    platform: string | null; account_handle: string | null; enabled: boolean
    state: string; failure_count: number; last_error: string | null
    last_success_at: string | null; next_check_at: string | null
    instagram_stories_enabled?: boolean; stories_last_collect_at?: string | null
    stories_source_status?: string | null
  }[]
  queries: { id: string; query_template: string; query_type: 'AUTO' | 'CUSTOM'; priority_level: string; enabled: boolean }[]
  coverage: Record<string, string>
}
interface Observability {
  sources_total: number; sources_healthy: number; queries_active: number
  events_known: number; signals_active: number; signals_high: number; errors_24h: number
  last_runs: { started_at: string; finished_at: string | null; status: string
    queries_attempted: number; results_found: number; signals_created: number
    signals_updated: number; errors: number }[]
}

const CATEGORY_OPTIONS = [
  { value: 'NIGHTCLUB', label: 'Boîte de nuit' }, { value: 'CLUB', label: 'Club' },
  { value: 'BAR', label: 'Bar' }, { value: 'BAR_NIGHTLIFE', label: 'Bar nightlife' },
  { value: 'CONCERT_VENUE', label: 'Salle de concert' }, { value: 'CULTURAL_VENUE', label: 'Lieu culturel' },
  { value: 'STUDENT_VENUE', label: 'Lieu étudiant' }, { value: 'STUDENT_EVENT_ORGANIZER', label: 'Organisateur étudiant / BDE' },
  { value: 'PROMOTER', label: 'Organisateur' }, { value: 'FESTIVAL', label: 'Festival' },
  { value: 'OTHER', label: 'Autre' },
]
const CLASS_LABELS: Record<string, string> = {
  DIRECT: 'concurrent direct', ADJACENT: 'nightlife adjacente',
  INDIRECT: 'concurrence indirecte', CONTEXTUAL: 'contextuel',
}
const CLASS_SECTIONS: { value: string; title: string }[] = [
  { value: 'DIRECT', title: 'Concurrents directs' },
  { value: 'ADJACENT', title: 'Nightlife adjacente' },
  { value: 'INDIRECT', title: 'Concurrence indirecte' },
  { value: 'CONTEXTUAL', title: 'Salles de concert / spectacle & autres signaux' },
]

/**
 * Les réglages Soirées tenaient sur une seule page qu'il fallait dérouler pour
 * trouver quoi que ce soit : saisons, familles, salles, veille, Instagram et
 * prévisions s'y succédaient sans hiérarchie. Chaque domaine a désormais SA
 * page, et `/settings/soirees` devient un hub qui les annonce.
 *
 * Le découpage est un choix d'AFFICHAGE : la configuration, son chargement et
 * sa barre d'enregistrement restent partagés. Dupliquer cette logique par page
 * aurait multiplié les occasions de la voir diverger.
 */
export type SectionSoirees =
  | 'saisons' | 'familles' | 'salles' | 'veille' | 'instagram' | 'recherche-web' | 'previsions' | 'caisse'

const TITRE_SECTION: Record<SectionSoirees, { titre: string; sous: string }> = {
  saisons: { titre: 'Saisons', sous: 'Bornes détectées sur l’historique réel, jamais sur un calendrier théorique.' },
  familles: { titre: 'Familles d’événements', sous: 'Les marques récurrentes qui alimentent comparaisons et prévisions.' },
  salles: { titre: 'Salles & DJs', sous: 'Le référentiel des salles et l’annuaire des DJs qui y jouent.' },
  veille: { titre: 'Veille concurrentielle', sous: 'Établissements surveillés, sources et cadence de relève.' },
  instagram: { titre: 'Instagram', sous: 'Connexion Meta, publications et Stories des concurrents.' },
  'recherche-web': { titre: 'Recherche web', sous: 'Moteur auto-hébergé — aucun abonnement, aucun coût.' },
  previsions: { titre: 'Prévisions', sous: 'Méthode déterministe, archivée et confrontée au réel.' },
  caisse: { titre: 'Caisse de la nuit', sous: 'Les rapports et le compté de la nuit, affichés en petit dans les Soirées.' },
}

/**
 * MÉMOIRE PRÉVISIONNELLE — où en est l'apprentissage, sans faux score.
 *
 * Ce panneau ne promet rien. Il compte : combien de nuits ont été figées
 * AVANT la soirée puis confrontées au réel, combien il en manque avant qu'un
 * backtest ait un sens, et de combien la méthode actuelle se trompe.
 *
 * Il n'affiche PAS de « IA 78 % » ni de barre de progression inventée. Tant
 * que la matière n'existe pas, il le dit — c'est la seule chose honnête à
 * afficher, et c'est déjà une information utile.
 */
function ForecastMemoryPanel() {
  const [m, setM] = useState<{
    usableNights: number; usableExamples: number; nightsNeeded: number
    horizons: { horizon: string; examples: number }[]
    baseline: { n: number; mae: number | null; mape: number | null; bias: number | null }
    status: 'PAS_DE_MATIERE' | 'ACCUMULATION' | 'BACKTEST_POSSIBLE'
    recordedOutcomes: number
  } | null>(null)
  useEffect(() => {
    apiGet<typeof m>('/api/v1/soirees/forecast-learning').then(setM).catch(() => undefined)
  }, [])
  if (!m) return null

  const seuil = m.usableNights + m.nightsNeeded
  const part = seuil > 0 ? Math.min(1, m.usableNights / seuil) : 0
  const etat = m.status === 'PAS_DE_MATIERE' ? 'Aucune nuit exploitable'
    : m.status === 'ACCUMULATION' ? 'Accumulation' : 'Backtest possible'

  return (
    <section className="lc-panel gd-panel" aria-labelledby="set-memory">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="set-memory">Mémoire prévisionnelle</h2>
        <span className="lc-hint">{etat}</span>
      </header>
      <div className="lc-panel__body fm-grid">
        <div className="fm-cell">
          <span className="fm-cell__v">{m.usableNights}</span>
          <span className="fm-cell__l">nuits exploitables</span>
          <span className="fm-jauge" aria-hidden="true"><i style={{ width: `${part * 100}%` }} /></span>
        </div>
        <div className="fm-cell">
          <span className="fm-cell__v">{m.nightsNeeded}</span>
          <span className="fm-cell__l">nuits encore nécessaires</span>
        </div>
        <div className="fm-cell">
          <span className="fm-cell__v">{m.baseline.mape === null ? '—' : `${m.baseline.mape} %`}</span>
          <span className="fm-cell__l">erreur de la méthode actuelle</span>
        </div>
        <div className="fm-cell">
          <span className="fm-cell__v">{m.recordedOutcomes}</span>
          <span className="fm-cell__l">nuits figées en mémoire</span>
        </div>
      </div>
      <div className="lc-panel__body">
        <p className="lc-hint">
          {m.status === 'PAS_DE_MATIERE'
            ? 'Aucune prévision figée avant une soirée n’a encore rencontré son résultat. Le système accumule ; il n’apprend pas, et il ne modifie aucune prévision.'
            : m.status === 'ACCUMULATION'
              ? 'Le système accumule des exemples. Aucun ajustement appris n’est appliqué : en dessous du seuil, une nuit atypique décide de tout.'
              : 'Assez de nuits pour un backtest en avançant dans le temps. Un modèle candidat resterait en observation avant toute activation, et sa promotion demanderait une validation humaine.'}
          {' '}La cible est le billet scanné — jamais un effectif présent.
        </p>
        {m.horizons.length > 0 ? (
          <p className="lc-hint">
            Horizons couverts : {m.horizons.map((h) => `${h.horizon} (${h.examples})`).join(' · ')}.
          </p>
        ) : null}
      </div>
    </section>
  )
}

export function SoireesSettingsPage({ section }: { section: SectionSoirees }) {
  const meta = useShellMeta()

  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  const toast = useToast()
  const [cfg, setCfg] = useState<SettingsPayload | null>(null)
  const [families, setFamilies] = useState('')
  const [minGap, setMinGap] = useState(14)
  const [marketEnabled, setMarketEnabled] = useState(true)
  const [radius, setRadius] = useState(30)
  const [keywords, setKeywords] = useState('')
  const [forecastEnabled, setForecastEnabled] = useState(true)
  const [caisse, setCaisse] = useState<CaisseReglages>(CAISSE_DEFAUT)
  const [venues, setVenues] = useState<VenueRow[]>([])
  const [obs, setObs] = useState<Observability | null>(null)
  const [venueToDisable, setVenueToDisable] = useState<VenueRow | null>(null)
  const [detail, setDetail] = useState<VenueDetail | null>(null)
  const [wizardOpen, setWizardOpen] = useState(false)
  /** Établissement en cours d'ÉDITION dans le wizard (null = création). */
  const [venueToEdit, setVenueToEdit] = useState<VenueRow | null>(null)
  const [venueToDelete, setVenueToDelete] = useState<VenueRow | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [scanTarget, setScanTarget] = useState<{ id: string; name: string; auto: boolean } | null>(null)

  const charger = () => {
    void apiGet<SettingsPayload>('/api/v1/soirees/settings').then((c) => {
      setCfg(c)
      setFamilies(c.families.join('\n'))
      setMinGap(c.seasons.min_gap_days)
      setMarketEnabled(c.market.enabled)
      setRadius(c.market.radius_km)
      setKeywords((c.market.keywords ?? []).join('\n'))
      setForecastEnabled(c.forecast.enabled)
      setCaisse({ ...CAISSE_DEFAUT, ...(c.night_cash ?? {}) })
    }).catch(() => setCfg(null))
    void apiGet<{ venues: VenueRow[] }>('/api/v1/soirees/market').then((m) => setVenues(m.venues ?? [])).catch(() => undefined)
    void apiGet<Observability>('/api/v1/soirees/market/observability').then(setObs).catch(() => undefined)
  }
  useEffect(charger, [])

  const ouvrirDetail = (id: string) => {
    void apiGet<VenueDetail>(`/api/v1/soirees/market/venues/${id}`).then(setDetail).catch(() => undefined)
  }

  const dirty = cfg !== null && peutGerer && (
    families !== cfg.families.join('\n')
    || minGap !== cfg.seasons.min_gap_days
    || marketEnabled !== cfg.market.enabled
    || radius !== cfg.market.radius_km
    || keywords !== (cfg.market.keywords ?? []).join('\n')
    || forecastEnabled !== cfg.forecast.enabled
    || !memeCaisse(caisse, { ...CAISSE_DEFAUT, ...(cfg.night_cash ?? {}) })
  )

  useSaveBar({
    dirty,
    saveLabel: 'Enregistrer les réglages Soirées',
    onSave: async () => {
      const r = await adminMutate<{ ok?: boolean; error?: string }>('PUT', '/api/v1/soirees/settings', {
        families: families.split('\n').map((f) => f.trim()).filter(Boolean),
        seasons_min_gap_days: minGap,
        market_enabled: marketEnabled,
        market_radius_km: radius,
        market_keywords: keywords.split('\n').map((k) => k.trim()).filter(Boolean),
        forecast_enabled: forecastEnabled,
        night_cash: caisse,
      })
      if (r.status !== 200) return 'Enregistrement impossible.'
      charger()
      return true
    },
    onCancel: charger,
    successMessage: 'Réglages Soirées enregistrés.',
  })

  const basculerVenue = async (v: VenueRow, enabled: boolean) => {
    const r = await adminMutate<{ ok?: boolean; error?: string }>('PATCH', `/api/v1/soirees/market/venues/${v.id}`, { enabled })
    if (r.status === 200) charger()
  }

  const supprimerVenue = async (v: VenueRow) => {
    setDeleting(true)
    const r = await adminMutate<{ ok?: boolean; error?: string; message?: string }>(
      'DELETE', `/api/v1/soirees/market/venues/${v.id}`, {})
    setDeleting(false)
    setVenueToDelete(null)
    if (r.status === 200) {
      toast('ok', `« ${v.name} » a été supprimé de la veille.`)
      charger()
    } else {
      // 409 = un scan tourne encore sur cette fiche (§34).
      toast('error', r.data?.message ?? 'Suppression impossible.')
    }
  }

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger">
        <SettingsCrumb />
        <h1 className="lc-title">
          <Sparkles size={26} aria-hidden="true" /> {TITRE_SECTION[section].titre}
        </h1>
        <p className="lc-subtitle">{TITRE_SECTION[section].sous}</p>

        {/* ─── Saisons ─── */}
        {section === 'saisons' ? (<>
        <section className="lc-panel gd-panel" aria-labelledby="set-saisons">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="set-saisons">Saisons</h2></header>
          <div className="lc-panel__body">
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Détection automatique par la coupure estivale (au moins 2 week-ends sans soirée).
              Les bornes viennent de l'historique réel — jamais d'un « 1er septembre » naïf.
            </p>
            <div className="rb-cond">
              <Field label="Coupure minimale (jours)" helper="Durée minimale de la fermeture estivale détectée.">
                <NumberInput value={minGap} onChange={(v) => setMinGap(Number(v) || 14)} min={7} max={60} unit="jours" ariaLabel="Coupure minimale en jours" />
              </Field>
            </div>
            <dl className="lc-kv" style={{ marginTop: 14 }}>
              {(cfg?.detected_seasons ?? []).map((s) => (
                <div key={s.name}>
                  <dt>Saison {s.name}</dt>
                  <dd>
                    {s.start} → {s.end} · {s.events_count ?? '—'} soirée(s)
                    {s.boundary_source === 'FALLBACK' ? ' · bornes par défaut (aucune coupure détectée)' : ''}
                    {s.boundary_source === 'MANUAL' ? ' · bornes manuelles' : ''}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section></>) : null}

        {/* ─── Familles (v4.1 : vraie interface dédiée) ─── */}
        {section === 'familles' ? (<>
        <section className="lc-panel gd-panel" aria-labelledby="set-familles">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="set-familles">Familles d'événements</h2></header>
          <div className="lc-panel__body">
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Les familles (THE CULTURE, L'ETUDIANTE…) alimentent les comparaisons et les
              prévisions. Les marques récurrentes de l'historique sont détectées
              automatiquement — confirmez-les dans l'interface dédiée.
            </p>
            <a className="lc-btn" href="/settings/soirees/familles">Gérer les familles et les suggestions</a>
          </div>
        </section></>) : null}

        {/* ─── Salles ─── */}
        {section === 'salles' ? <RoomsDjsPanel peutGerer={peutGerer} /> : null}
        {false ? (<>
        <section className="lc-panel gd-panel" aria-labelledby="set-salles">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="set-salles">Salles</h2></header>
          <div className="lc-panel__body">
            <dl className="lc-kv">
              {Object.entries(cfg?.mc_rooms ?? {}).map(([empl, code]) => (
                <div key={empl}><dt>Emplacement Main Courante {empl}</dt><dd>{code}</dd></div>
              ))}
            </dl>
            <p className="lc-hint">
              Quand le titre Shotgun ne précise pas de salle et que la Main Courante confirme
              une salle unique ouverte, elle est attribuée automatiquement (provenance tracée).
            </p>
          </div>
        </section></>) : null}

        {/* ─── Veille marché (v4) ─── */}
        {section === 'veille' ? (<>
        <section className="lc-panel gd-panel" aria-labelledby="set-market">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="set-market">Veille concurrentielle</h2></header>
          <div className="lc-panel__body">
            <SwitchRow checked={marketEnabled} onChange={setMarketEnabled} disabled={!peutGerer}
              title="Veille activée"
              desc="Collecte automatique par le worker (sites, agendas, flux) + extraction déterministe de signaux — fonctionne sans aucune IA." />
            <div className="rb-cond" style={{ marginTop: 12 }}>
              <Field label="Rayon autour de Rennes" helper="Périmètre géographique de la veille.">
                <NumberInput value={radius} onChange={(v) => setRadius(Number(v) || 30)} min={5} max={100} unit="km" ariaLabel="Rayon en kilomètres" />
              </Field>
            </div>
            <Field label="Mots-clés surveillés (un par ligne)"
              helper="Concepts qui déclenchent l'attention : gratuité, guest, étudiant, sold out… Les recherches automatiques et l'extraction s'appuient dessus.">
              <textarea className="lc-input" rows={6} value={keywords}
                onChange={(e) => setKeywords(e.target.value)} disabled={!peutGerer}
                aria-label="Mots-clés de veille" />
            </Field>

            <h3 className="so-month__name" style={{ marginTop: 18 }}>Établissements surveillés</h3>
            {CLASS_SECTIONS.filter((cs) => venues.some((v) => v.competition_class === cs.value)).map((cs) => (
              <div key={cs.value}>
                <h4 className="so-class-title">{cs.title}</h4>
                <div className="so-venues">
                  {venues.filter((v) => v.competition_class === cs.value).map((v) => (
                <div key={v.id} className="so-venue">
                  <button type="button" className="so-venue__body so-clickable" onClick={() => ouvrirDetail(v.id)}
                    aria-label={`Ouvrir la fiche de ${v.name}`}>
                    <span className="so-venue__name">
                      {v.name}
                      {v.priority_level === 'HIGH' ? <em className="so-venue__prio"> · priorité haute</em> : null}
                      {!v.enabled ? <em className="so-venue__prio"> · désactivé</em> : null}
                    </span>
                    {/* §37-§39 — l'information MÉTIER d'abord. « 1 source(s) ·
                        17 recherche(s) » est un détail technique : il vit dans
                        la fiche, pas en tête de ligne. */}
                    <span className="lc-hint">
                      {[
                        CATEGORY_OPTIONS.find((c) => c.value === v.category)?.label ?? v.category,
                        CLASS_LABELS[v.competition_class] ?? null,
                        v.priority_level === 'HIGH' ? 'priorité haute'
                          : v.priority_level === 'LOW' ? 'priorité basse' : null,
                        v.distance_km !== null ? `${Number(v.distance_km).toLocaleString('fr-FR')} km` : null,
                        v.status === 'DISCOVERED' ? 'à valider' : null,
                      ].filter(Boolean).join(' · ')}
                    </span>
                    {/* §21 — dernier scan, ce qu'il a donné, et quand revient
                        le prochain automatique. Trois faits, une ligne. */}
                    <span className="so-venue__scan">
                      {v.scanning
                        ? 'Scan en cours…'
                        : v.last_scan
                          ? `Dernier scan : ${distanceFr(v.last_scan.at)} · ${
                              v.last_scan.new_signals > 0
                                ? `${v.last_scan.new_signals} nouveau${v.last_scan.new_signals > 1 ? 'x' : ''} signal${v.last_scan.new_signals > 1 ? 'aux' : ''}`
                                : v.last_scan.signals > 0
                                  ? `${v.last_scan.signals} signal(aux) actualisé(s)`
                                  : 'rien de nouveau'
                            }${v.last_scan.errors > 0 ? ` · ${v.last_scan.errors} erreur(s)` : ''}`
                          : 'Jamais scanné'}
                      {!v.scanning && v.last_scan?.next_at
                        ? ` · prochaine collecte ${distanceFr(v.last_scan.next_at, true)}`
                        : ''}
                    </span>
                    {/* Une erreur se dit, mais après le reste : elle ne doit
                        pas être la première chose qu'on lit sur un lieu. */}
                    {v.last_error ? (
                      <span className="lc-hint lc-hint--warn so-venue__err">
                        {v.last_error.slice(0, 70)}
                      </span>
                    ) : null}
                  </button>
                  {peutGerer && v.status === 'DISCOVERED' ? (
                    <button type="button" className="lc-btn lc-btn--primary"
                      onClick={() => void adminMutate('PATCH', `/api/v1/soirees/market/venues/${v.id}`, { status: 'APPROVED' }).then(charger)}>
                      Valider la surveillance
                    </button>
                  ) : null}
                </div>
              ))}
                </div>
              </div>
            ))}
            {venues.length === 0 ? <p className="lc-hint">Aucun établissement surveillé pour l'instant — ajoutez les concurrents qui comptent.</p> : null}

            {peutGerer ? (
              <div className="so-venue-add so-venue-add--cta">
                <button type="button" className="lc-btn lc-btn--primary" onClick={() => setWizardOpen(true)}>
                  <Plus size={14} aria-hidden="true" /> Ajouter un établissement
                </button>
                <p className="lc-hint" style={{ margin: 0 }}>
                  Recherche, sources publiques et recherches de veille sont proposées étape par étape.
                </p>
              </div>
            ) : null}

            {/* ─── Observabilité (§60) ─── */}
            {obs ? (
              <>
                <h3 className="so-month__name" style={{ marginTop: 18 }}>État de la veille</h3>
                <dl className="lc-kv">
                  <div><dt>Sources saines</dt><dd>{obs.sources_healthy} / {obs.sources_total}</dd></div>
                  <div><dt>Recherches actives</dt><dd>{obs.queries_active}</dd></div>
                  <div><dt>Événements suivis</dt><dd>{obs.events_known}</dd></div>
                  <div><dt>Signaux actifs</dt><dd>{obs.signals_active}{obs.signals_high > 0 ? ` (dont ${obs.signals_high} à fort impact)` : ''}</dd></div>
                  <div><dt>Erreurs (24 h)</dt><dd>{obs.errors_24h}</dd></div>
                  <div><dt>Dernière passe</dt><dd>{obs.last_runs[0]
                    ? `${new Date(obs.last_runs[0].started_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })} · ${obs.last_runs[0].status} · ${obs.last_runs[0].results_found} résultat(s)`
                    : 'jamais'}</dd></div>
                </dl>
              </>
            ) : null}
          </div>
        </section></>) : null}

        {/* ─── Provider social (Instagram) ─── */}
        {section === 'instagram' ? <InstagramProviderPanel /> : null}
        {section === 'recherche-web' ? <WebSearchPanel peutGerer={peutGerer} /> : null}

        {/* ─── Prévisions ─── */}
        {section === 'previsions' ? (<>
        <section className="lc-panel gd-panel" aria-labelledby="set-forecast">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="set-forecast">Prévisions</h2></header>
          <div className="lc-panel__body">
            <SwitchRow checked={forecastEnabled} onChange={setForecastEnabled} disabled={!peutGerer}
              title="Prévisions activées"
              desc="Méthode déterministe et explicable (comparables pondérés × rythme de préventes) — chaque prévision émise est archivée, immuable, et comparée au réel après la soirée." />
          </div>
        </section>
        <ForecastMemoryPanel /></>) : null}

        {/* ─── Caisse de la nuit ─── */}
        {section === 'caisse' ? (
        <section className="lc-panel gd-panel" aria-labelledby="set-caisse">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="set-caisse">Caisse de la nuit</h2></header>
          <div className="lc-panel__body">
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Le total des rapports de caisse et le total compté de la nuit, en petit, là où l’équipe
              regarde les soirées. Ce sont les chiffres de la fiche de la nuit en Comptabilité : une nuit
              qui porte deux soirées montre les mêmes chiffres sur les deux. Seuls les comptes qui lisent
              les clôtures et les montants de la Comptabilité les voient ; pour les autres, rien ne change.
            </p>
            <SwitchRow checked={caisse.soiree_page} onChange={(v) => setCaisse({ ...caisse, soiree_page: v })} disabled={!peutGerer}
              title="Sur la fiche d’une soirée"
              desc="Une petite tuile « Caisse de la nuit » en bas de la page, une fois la soirée lancée." />
            <SwitchRow checked={caisse.week_cards} onChange={(v) => setCaisse({ ...caisse, week_cards: v })} disabled={!peutGerer}
              title="Sur les cartes « Dernier week-end » et « Cette semaine »"
              desc="Une ligne discrète sous les billets scannés de chaque soirée passée." />
            <SwitchRow checked={caisse.history} onChange={(v) => setCaisse({ ...caisse, history: v })} disabled={!peutGerer}
              title="Dans la liste de toutes les soirées"
              desc="La même ligne sous chaque soirée de l’historique." />
            <SwitchRow checked={caisse.calendar} onChange={(v) => setCaisse({ ...caisse, calendar: v })} disabled={!peutGerer}
              title="Dans le calendrier"
              desc="Dans la bulle et la fiche rapide d’une soirée, et sous chaque soirée de l’agenda." />
            <SwitchRow checked={caisse.closed_revenue} onChange={(v) => setCaisse({ ...caisse, closed_revenue: v })} disabled={!peutGerer}
              title="Afficher le CA arrêté ou validé"
              desc="Une nuit finalisée montre son CA arrêté à côté des rapports et du compté. Une nuit reprise de l’historique n’a que son CA validé : c’est lui qui s’affiche, à la place des rapports." />
          </div>
        </section>
        ) : null}

        <ConfirmDialog
          open={venueToDisable !== null}
          danger
          title="Désactiver la surveillance ?"
          confirmLabel="Désactiver"
          onConfirm={() => { if (venueToDisable) void basculerVenue(venueToDisable, false); setVenueToDisable(null) }}
          onClose={() => setVenueToDisable(null)}
        >
          <p>{venueToDisable?.name} ne sera plus relevé. Ses signaux existants restent visibles
            jusqu'à péremption — rien n'est supprimé.</p>
        </ConfirmDialog>

        {/* §30 — suppression définitive : ce qui part et ce qui reste, dit
            avant le clic. L'historique des prévisions n'est jamais réécrit. */}
        <ConfirmDialog
          open={venueToDelete !== null}
          danger
          title={`Supprimer ${venueToDelete?.name} ?`}
          confirmLabel={deleting ? 'Suppression…' : 'Supprimer définitivement'}
          onConfirm={() => { if (venueToDelete) void supprimerVenue(venueToDelete) }}
          onClose={() => setVenueToDelete(null)}
        >
          <p>
            La surveillance et sa configuration seront supprimées :
            {venueToDelete ? ` ${venueToDelete.sources_count} source(s) et ${venueToDelete.queries_count} recherche(s)` : ''}.
          </p>
          <p>
            L'historique des prévisions déjà générées restera intact, et les signaux
            déjà produits garderont le nom de l'établissement pour rester lisibles.
          </p>
        </ConfirmDialog>

        <VenueWizard
          open={wizardOpen || venueToEdit !== null}
          venueId={venueToEdit?.id ?? null}
          onClose={() => { setWizardOpen(false); setVenueToEdit(null) }}
          onCreated={charger}
          onScanRequested={(id) => {
            const v = venues.find((x) => x.id === id)
            setScanTarget({ id, name: v?.name ?? 'Établissement', auto: true })
          }}
        />

        <VenueScanDrawer
          open={scanTarget !== null}
          venueId={scanTarget?.id ?? null}
          venueName={scanTarget?.name ?? ''}
          autoStart={scanTarget?.auto ?? false}
          onClose={() => setScanTarget(null)}
          onFinished={charger}
        />

        {/* §17 — CONSULTATION d'abord : cliquer sur un établissement ouvre une
            fiche de monitoring, pas un formulaire de vingt-quatre champs.
            L'édition est un MODE de cette même modale. */}
        <VenueDetailModal
          venueId={detail?.venue.id ?? null}
          open={detail !== null}
          onClose={() => setDetail(null)}
          canManage={peutGerer}
          onChanged={charger}
        />
      </main>
    </Shell>
  )
}

/* ═══════════ fiche établissement : profil + sources + recherches ═══════════ */

